import { useMemo, useState } from "react"
import { Layout } from "../components/Layout"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useUploads, useUploadStats, useUploadSession } from "@/hooks/useUploads"
import type { UploadBatch, UploadSession } from "@/hooks/useUploads"
import { filterUploadSessions } from "@/lib/filterUploadSessions"
import { formatBytes } from "@/utils/formatters"
import {
  ArrowUpTrayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline"

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-600",
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-600"}`}>
      {status.replace("_", " ")}
    </span>
  )
}

function SourceBadge({ source }: { source: string }) {
  const styles: Record<string, string> = {
    web: "bg-purple-100 text-purple-800",
    mobile: "bg-teal-100 text-teal-800",
    desktop: "bg-indigo-100 text-indigo-800",
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[source] || "bg-gray-100 text-gray-600"}`}>
      {source || "—"}
    </span>
  )
}

function StatCard({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 text-secondary text-sm mb-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-primary">{value}</div>
    </div>
  )
}

function BatchProgress({ batch }: { batch: UploadBatch }) {
  const { completed_items, total_items, total_bytes, failed_items } = batch
  const pct =
    total_items > 0 ? Math.round((completed_items / total_items) * 100) : 0
  const sizeLabel = total_bytes != null ? formatBytes(total_bytes) : null
  // File size + percent + parts surfaced on hover (#818); presigned uploads
  // advance the part count live via the throttled heartbeat.
  const hover = [
    sizeLabel ? `${sizeLabel} total` : null,
    `${pct}%`,
    `${completed_items} of ${total_items} parts`,
  ]
    .filter(Boolean)
    .join(" · ")
  return (
    <span className="text-sm" title={hover}>
      {completed_items}/{total_items}
      <span className="text-gray-400 ml-1">· {pct}%</span>
      {sizeLabel && <span className="text-gray-400 ml-1">· {sizeLabel}</span>}
      {failed_items > 0 && (
        <span className="text-red-500 ml-1">({failed_items} failed)</span>
      )}
    </span>
  )
}

// Activate an interactive element on Enter/Space, matching native button keys.
function onActivateKey(handler: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handler()
    }
  }
}

// One per-clip batch row, used both as a standalone upload and as an expanded
// child under a roll-up. `nested` indents + de-emphasizes the child rows.
function BatchRow({ batch, nested }: { batch: UploadBatch; nested?: boolean }) {
  const open = () => {
    window.location.href = `/uploads/${batch.public_id}`
  }
  return (
    <div
      className={`grid grid-cols-12 items-center gap-3 px-4 py-2 text-sm hover:bg-surface-hover cursor-pointer ${
        nested ? "bg-surface/50" : ""
      }`}
      onClick={open}
      onKeyDown={onActivateKey(open)}
      role="button"
      tabIndex={0}
    >
      <div className={`col-span-3 ${nested ? "pl-6" : ""}`}>
        <div className="font-medium text-primary truncate">
          {batch.user.name || "—"}
        </div>
        <div className="text-xs text-secondary truncate">{batch.user.email}</div>
      </div>
      <div className="col-span-2 font-mono text-secondary truncate">
        {batch.clip_public_id || batch.game_public_id || "—"}
      </div>
      <div className="col-span-1">
        <SourceBadge source={batch.source} />
      </div>
      <div className="col-span-1 truncate">{batch.phase}</div>
      <div className="col-span-2">
        <StatusBadge status={batch.status} />
        {batch.failure_reason && (
          <div
            className="text-xs text-red-500 mt-0.5 truncate"
            title={batch.failure_reason}
          >
            {batch.failure_reason}
          </div>
        )}
      </div>
      <div className="col-span-2">
        <BatchProgress batch={batch} />
      </div>
      <div className="col-span-1 text-xs text-secondary text-right">
        {new Date(batch.created_at).toLocaleString()}
      </div>
    </div>
  )
}

// A roll-up parent: the crop/upload session for one game (#1028). Counts are
// computed SERVER-side over the full dataset, so they're accurate even when the
// session spans more than one page. Expanding lazily fetches the member rows.
function RollupRow({ session }: { session: UploadSession }) {
  const [expanded, setExpanded] = useState(false)
  // Only fetch the children once the row is expanded.
  const { data: children, isLoading: childrenLoading } = useUploadSession(
    expanded ? session.session_id : null
  )
  const sizeLabel = session.totalBytes != null ? formatBytes(session.totalBytes) : null
  const started = new Date(session.startedAt)
  const ended = new Date(session.endedAt)
  const timeRange =
    session.startedAt === session.endedAt
      ? started.toLocaleString()
      : `${started.toLocaleString()} – ${ended.toLocaleTimeString()}`

  return (
    <div>
      <div
        className="grid grid-cols-12 items-center gap-3 px-4 py-2.5 text-sm hover:bg-surface-hover cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={onActivateKey(() => setExpanded((v) => !v))}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
      >
        <div className="col-span-3 flex items-center gap-1.5">
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4 text-secondary shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-secondary shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-primary truncate">
              {session.user.name || "—"}
            </div>
            <div className="text-xs text-secondary truncate">
              {session.user.email}
            </div>
          </div>
        </div>
        <div className="col-span-2 font-mono text-secondary truncate">
          {session.game_public_id || "—"}
        </div>
        <div className="col-span-1">
          <SourceBadge source={session.source} />
        </div>
        <div className="col-span-1 text-xs text-secondary">
          crop session
        </div>
        <div className="col-span-2">
          <StatusBadge status={session.status} />
        </div>
        <div className="col-span-2 text-sm">
          <span className="font-medium text-primary">{session.itemCount} items</span>
          <span className="text-green-600 ml-1">· {session.completed} ok</span>
          {session.failed > 0 && (
            <span className="text-red-500 ml-1">· {session.failed} failed</span>
          )}
          {session.cancelled > 0 && (
            <span className="text-gray-500 ml-1">· {session.cancelled} cancelled</span>
          )}
          {sizeLabel && <span className="text-gray-400 ml-1">· {sizeLabel}</span>}
        </div>
        <div className="col-span-1 text-xs text-secondary text-right" title={timeRange}>
          {timeRange}
        </div>
      </div>
      {expanded && (
        <div className="border-l-2 border-border ml-4">
          {childrenLoading ? (
            <div className="px-4 py-2 text-xs text-secondary pl-6">Loading members…</div>
          ) : (
            children?.members.map((batch) => (
              <BatchRow key={batch.public_id} batch={batch} nested />
            ))
          )}
        </div>
      )}
    </div>
  )
}

function LoadMoreButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-primary hover:bg-background disabled:opacity-50"
    >
      {loading ? "Loading…" : "Load more"}
    </button>
  )
}

export function Uploads() {
  const [source, setSource] = useState<"all" | "web" | "mobile" | "desktop">("all")
  const [statusFilter, setStatusFilter] = useState<
    "all" | "in_progress" | "completed" | "failed" | "cancelled"
  >("all")
  const [search, setSearch] = useState("")
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useUploads({ source, status: statusFilter })
  const { data: stats } = useUploadStats()

  // The server already returns crop-session roll-ups with accurate full-dataset
  // counts (#1028); flatten the loaded pages into one session list.
  const sessions = useMemo(
    () => data?.pages.flatMap((p) => p.sessions) ?? [],
    [data?.pages]
  )

  // Client-side user/email search — admins rely on it to find a specific
  // coach's upload. Filters the loaded sessions (matches name or email,
  // case-insensitive).
  const filteredSessions = useMemo(
    () => filterUploadSessions(sessions, search),
    [sessions, search]
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">Uploads</h1>
          <p className="text-secondary">Monitor upload batches and Live Activity sessions</p>
        </div>

        {/* Filters (search / source / status). Backend + useUploads already
            support source/status; search is a client-side filter over the
            loaded rows. */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-secondary">
            Search{" "}
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="user name or email"
              className="ml-1 rounded border border-border bg-surface px-2 py-1 text-sm text-primary"
            />
          </label>
          <label className="text-sm text-secondary">
            Source{" "}
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as typeof source)}
              className="ml-1 rounded border border-border bg-surface px-2 py-1 text-sm text-primary"
            >
              <option value="all">All</option>
              <option value="web">Web</option>
              <option value="mobile">Mobile</option>
              <option value="desktop">Desktop</option>
            </select>
          </label>
          <label className="text-sm text-secondary">
            Status{" "}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="ml-1 rounded border border-border bg-surface px-2 py-1 text-sm text-primary"
            >
              <option value="all">All</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            <StatCard
              label="Active batches"
              value={stats.batches.active}
              icon={<ArrowUpTrayIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Last hour"
              value={stats.batches.lastHour}
              icon={<ClockIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Failed"
              value={stats.batches.failed}
              icon={<XCircleIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Active sessions"
              value={stats.sessions.active}
              icon={<CheckCircleIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Invalid tokens"
              value={stats.sessions.invalid}
              icon={<XCircleIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Sessions w/ pushes"
              value={stats.sessions.withPushes}
              icon={<CheckCircleIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Avg revisions sent"
              value={stats.sessions.avgRevisions}
              icon={<ClockIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Total rows"
              value={stats.batches.total + stats.sessions.total + stats.devices.total}
              icon={<ExclamationTriangleIcon className="h-4 w-4" />}
            />
          </div>
        )}

        {/* Abuse detection warning */}
        {stats && stats.batches.lastHour > 50 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              High upload volume: {stats.batches.lastHour} batches in the last hour
            </span>
          </div>
        )}

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center gap-3">
            <EmptyState
              icon={<ArrowUpTrayIcon className="h-12 w-12" />}
              title="No uploads"
              description={
                search.trim() && hasNextPage
                  ? "No matches in loaded uploads — load more to search older uploads."
                  : source !== "all" || statusFilter !== "all" || search.trim()
                    ? "No uploads match the current filter."
                    : "No upload batches have been created yet."
              }
            />
            {/* Without this the empty-state's "load more" hint is a dead end: the
                footer's Load more only renders in the non-empty branch. (Codex) */}
            {hasNextPage && (
              <LoadMoreButton onClick={() => fetchNextPage()} loading={isFetchingNextPage} />
            )}
          </div>
        ) : (
          <div className="bg-surface rounded-lg border border-border overflow-hidden">
            {/* Column header. Rows below are crop-session roll-ups (expandable);
                each computed server-side over the full dataset (#1028). */}
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs font-medium uppercase tracking-wide text-secondary border-b border-border">
              <div className="col-span-3">User</div>
              <div className="col-span-2">Game / Clip</div>
              <div className="col-span-1">Source</div>
              <div className="col-span-1">Phase</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Progress</div>
              <div className="col-span-1 text-right">When</div>
            </div>
            <div className="divide-y divide-border">
              {filteredSessions.map((session) =>
                // A one-off upload renders as a standalone batch row (phase /
                // progress / direct detail link), not a collapsed "crop session".
                !session.isRollup && session.batch ? (
                  <BatchRow key={session.session_id} batch={session.batch} />
                ) : (
                  <RollupRow key={session.session_id} session={session} />
                )
              )}
            </div>
            {/* Pagination: sessions are paginated server-side, so "Load more"
                pages back through history. Search filters the loaded sessions
                client-side, so loading more can surface older matches (#1028). */}
            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5 text-xs text-secondary">
              <span>
                {sessions.length} session{sessions.length === 1 ? "" : "s"} loaded
                {search.trim() ? ` · ${filteredSessions.length} match search` : ""}
                {search.trim() && filteredSessions.length === 0 && hasNextPage
                  ? " — Load more to search older uploads"
                  : ""}
              </span>
              {hasNextPage && (
                <LoadMoreButton onClick={() => fetchNextPage()} loading={isFetchingNextPage} />
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}
